/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from "react-hot-toast"
import {
  selectAdasProgramsMetaData, selectSensingSystemRequirementsEditReportDialogOpen,
  selectSensingSystemRequirementsTableDataSetStatusMap,
  selectSensorComponentRequirementsEditReportDialogOpen,
  selectSensorComponentRequirementsTableDataSetStatusMap,
  selectWorldModelSolutionRequirementsEditReportDialogOpen,
  selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  setSensingSystemRequirementsEditReportDialogOpen,
  setSensorComponentRequirementsEditReportDialogOpen,
  setWorldModelSolutionRequirementsEditReportDialogOpen
} from "../../../../../../redux/ducks/AdasPrograms/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks"
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog"
import { SELECT_DATASET_DIALOG_STYLE } from "../../../../../components/Tree/Parts/JamaTreeContainer"
import { AdasProgramsReportPageIdx, AdasProgramsReportPages } from "../constants"
import { usePostRequest } from "../../../../../../api/helper"
import { CAdasProgramsRequests } from "../../types"
import { EArgosReportsState } from "../../../../../../constants/colors"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import { StringParam, useQueryParam } from "use-query-params"
import { useMemo } from "react"
import { getPageFromHtabIndex } from "../helpers"
import { ReportWizard } from "./ReportWizard"


export const editReportDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    isDialogOpen: selectSensingSystemRequirementsEditReportDialogOpen,
    setIsDialogOpen: setSensingSystemRequirementsEditReportDialogOpen,
    dataSetStatusMap: selectSensingSystemRequirementsTableDataSetStatusMap,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    isDialogOpen: selectSensorComponentRequirementsEditReportDialogOpen,
    setIsDialogOpen: setSensorComponentRequirementsEditReportDialogOpen,
    dataSetStatusMap: selectSensorComponentRequirementsTableDataSetStatusMap,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    isDialogOpen: selectWorldModelSolutionRequirementsEditReportDialogOpen,
    setIsDialogOpen: setWorldModelSolutionRequirementsEditReportDialogOpen,
    dataSetStatusMap: selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  },
}

export const EditReportDialog = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const isOpen = useAppSelector(editReportDialogSlice[page].isDialogOpen)
  const closeDialog = () => {dispatch(editReportDialogSlice[page].setIsDialogOpen(false))}
  const dataSetStatusMap = useAppSelector(editReportDialogSlice[page].dataSetStatusMap)

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  /** Request handles */
  const editReportDataHandle = usePostRequest(CAdasProgramsRequests.editCoverageRequirementReportData.url)
  const editReportErrorMsg = "An error occurred while editing the report"
  const editReportInProgressError = "Report is already in progress. Please wait for it to complete before editing."
  const editReportPendingError = "Report is Pending. Please update the data set before editing."

  /** Edit report action */
  const editReport = (selectedReportData: any) => {
    /** Check input conditions are valid */
    const _selectedReportId = selectedReportData?.reportId
    if (_selectedReportId == null) {
      closeDialog()
      return
    }
    if (dataSetStatusMap?.[_selectedReportId] === EArgosReportsState.IN_PROGRESS) {
      toast.error(editReportInProgressError)
      closeDialog()
      return
    }
    /** TODO: Decide how to handle this case */
    // else if(reportIdToDatasetStatusMap?.[_selectedReportId] === EArgosReportsState.PENDING) {
    //   toast.error(editReportPendingError)
    //   closeDialog()
    //   return
    // }

    /** Close dialog, table will show loading state */
    closeDialog()

    /** Edit report data item */
    editReportDataHandle({
      action: 'editReport',
      page,
      adasProgramId,
      selectedReportData,
    }).then().catch((err) => {
      console.log(editReportErrorMsg, err)
      toast.error(editReportErrorMsg)
    })
  }


  return (
    <ArgosDialog
      {...SELECT_DATASET_DIALOG_STYLE}
      open={isOpen}
      onClose={closeDialog}
      headerTitle="Edit Report"
    >
      <ReportWizard
        page={page}
        onSave={(selectedReportData: any) => {editReport(selectedReportData)}}
        onClickClose={closeDialog}
        isEdit={true}
        populateFields={true}
      />
    </ArgosDialog>
  )
}
